import React from "react";

import type { CoverageCell, CoverageStatus } from "@/lib/telemetry";
import { formatTimeOnly, type TelemetryTz } from "@/components/telemetry/format";
import { formatPercent } from "@voltie/localization-web";
import { useT } from "@/app/localization";

/**
 * `empty` and `not_archived` are deliberately different colours and different
 * words. An hour with no rows OLDER than the watermark means the charger was
 * silent; the same hour NEWER than the watermark means the feeder has not got
 * there yet. Merging them is the fastest way this page manufactures a false
 * incident, so `unknown` (no watermark at all) is its own state as well.
 */
const STATUS_STYLE: Record<CoverageStatus, { cell: string }> = {
  full: { cell: "bg-emerald-500/80" },
  partial: { cell: "bg-amber-400/80" },
  empty: { cell: "bg-destructive/70" },
  not_archived: {
    cell: "bg-muted-foreground/25 [background-image:repeating-linear-gradient(45deg,transparent,transparent_3px,rgba(0,0,0,.12)_3px,rgba(0,0,0,.12)_6px)]",
  },
  unknown: { cell: "bg-muted-foreground/15" },
};

function cellRange(cell: CoverageCell): { from: string; to: string } {
  const start = cell.hour !== undefined
    ? new Date(`${cell.dt}T${cell.hour.padStart(2, "0")}:00:00Z`)
    : new Date(`${cell.dt}T00:00:00Z`);
  const stepMs = cell.hour !== undefined ? 3_600_000 : 86_400_000;
  return {
    from: start.toISOString().replace(/\.\d+Z$/, "Z"),
    to: new Date(start.getTime() + stepMs).toISOString().replace(/\.\d+Z$/, "Z"),
  };
}

export function CoverageStrip({
  cells,
  tz,
  onSelectRange,
}: {
  cells: CoverageCell[];
  tz: TelemetryTz;
  /** Clicking a cell narrows the window to it — one hour is the cheapest scan. */
  onSelectRange?: (range: { from: string; to: string }) => void;
}) {
  const t = useT();
  if (!cells.length) {
    return <div className="text-sm text-muted-foreground">{t("operator.telemetry.coverage_empty")}</div>;
  }

  const present = new Set(cells.map((c) => c.status));

  return (
    <div data-testid="telemetry-coverage">
      <div className="flex flex-wrap gap-[3px]">
        {cells.map((cell) => {
          const key = `${cell.dt}${cell.hour ?? ""}`;
          const range = cellRange(cell);
          const pct = cell.completeness == null ? "—" : formatPercent(cell.completeness);
          // The cell's own UTC partition (date, hour) is a technical label and stays as stored.
          const title = [
            cell.hour !== undefined ? `${cell.dt} ${cell.hour}:00 UTC` : cell.dt,
            t("operator.telemetry.cell_samples", {
              status: cell.status,
              samples: cell.samples,
              expected: cell.expected,
              percent: pct,
            }),
            cell.first_ts ? t("operator.telemetry.cell_first", { time: formatTimeOnly(cell.first_ts, tz) }) : null,
            cell.last_ts ? t("operator.telemetry.cell_last", { time: formatTimeOnly(cell.last_ts, tz) }) : null,
            cell.commands ? t("operator.telemetry.commands_count", { count: cell.commands }) : null,
            cell.errors ? t("operator.telemetry.error_samples_count", { count: cell.errors }) : null,
            cell.states.length ? t("operator.telemetry.cell_states", { states: cell.states.join(", ") }) : null,
          ]
            .filter(Boolean)
            .join("\n");

          return (
            <button
              key={key}
              type="button"
              title={title}
              aria-label={title}
              data-testid={`coverage-cell-${key}`}
              data-status={cell.status}
              onClick={onSelectRange ? () => onSelectRange(range) : undefined}
              className={`h-7 w-[18px] rounded-[3px] transition-opacity hover:opacity-70 ${STATUS_STYLE[cell.status]?.cell ?? STATUS_STYLE.unknown.cell}`}
            />
          );
        })}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
        {(Object.keys(STATUS_STYLE) as CoverageStatus[])
          .filter((status) => present.has(status))
          .map((status) => (
            <span key={status} className="flex items-center gap-1.5">
              <span className={`inline-block h-2.5 w-2.5 rounded-[2px] ${STATUS_STYLE[status].cell}`} />
              {t("operator.telemetry.coverage_status", { status })}
            </span>
          ))}
        {onSelectRange && <span className="opacity-70">{t("operator.telemetry.coverage_click_hint")}</span>}
      </div>
    </div>
  );
}
